/**
 * Renders a fill-style progress indicator for the nearest ancestor
 * `slideshow-component`, growing from the left edge as the slides scroll.
 */
class VanityProgressBar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #thumb = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.#thumb = this.querySelector('.vanity-progress-bar__thumb');

    const slideshow = this.closest('slideshow-component');
    this.#scroller = slideshow?.querySelector('slideshow-slides') ?? null;

    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#update, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#scroller);

    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  #update = () => {
    const scroller = this.#scroller;
    const thumb = this.#thumb;
    if (!scroller || !thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    if (scrollWidth <= 0) return;

    const maxScroll = scrollWidth - clientWidth;
    // The bar always starts filled by the portion already visible, then grows
    // to 100% once the last slide is reached.
    const filled = maxScroll > 0 ? (Math.abs(scrollLeft) + clientWidth) / scrollWidth : 1;
    const fillPercent = Math.min(100, Math.max(0, filled * 100));

    thumb.style.width = `${fillPercent}%`;
  };
}

if (!customElements.get('vanity-progress-bar')) {
  customElements.define('vanity-progress-bar', VanityProgressBar);
}
